import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import cn from 'classnames';

import {
  COUNTRIES,
  DEFAULT_COUNTRY_CODE,
  DEFAULT_STYLES,
  KEYBOARD_KEYS
} from '../../constants';
import { CountryOption, PhoneNumberFieldProps } from '../../types';

import { Portal } from '../Portal';

export const PhoneNumberField: React.FC<PhoneNumberFieldProps> = (props) => {
  const {
    name,
    label,
    phoneNumber = '',
    countryCode = DEFAULT_COUNTRY_CODE,
    onChangePhoneNumber,
    onChangeCountryCode,
    onChangeDialCode,
    onBlur,
    onFocus,
    error,
    helperText,
    isDisabled = false,
    isRequired = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    allowedCountries, // Reserved for future country filtering
    placeholder = 'Enter phone number',
    className,
    labelClassName,
    inputClassName,
    phoneFieldContainerClassName,
    phoneFieldCountryBtnClassName,
    phoneFieldCountryTextClassName,
    phoneFieldDropdownIconClassName,
    phoneFieldDropdownClassName,
    phoneFieldDropdownListClassName,
    phoneFieldOptionBtnClassName,
    phoneFieldCountryNameClassName,
    phoneFieldDialCodeClassName,
    phoneFieldCheckmarkClassName,
    phoneFieldErrorTextClassName,
    phoneFieldHelperTextClassName,
    rightElement,
    rightButtonText,
    rightButtonAction,
    externalButtonText,
    externalButtonAction,
    externalButtonClassName,
    externalButtonElement
  } = props;

  const inputId = useId();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownListRef = useRef<HTMLDivElement>(null); // Ref for scrollable country list
  const inputRef = useRef<HTMLInputElement>(null);

  // Explicitly compute if external button should be shown
  const hasExternalButton =
    Boolean(externalButtonElement) || Boolean(externalButtonText);

  // Get current selected country
  const selectedCountry =
    COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0];

  // Calculate dropdown position based on trigger element
  const updateDropdownPosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 2,
        left: rect.left,
        zIndex: 9999
      });
    }
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideTrigger =
        triggerRef.current && !triggerRef.current.contains(target);
      const isOutsideDropdown =
        dropdownRef.current && !dropdownRef.current.contains(target);

      if (isOutsideTrigger && isOutsideDropdown) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update position when dropdown opens and on scroll/resize
  useEffect(() => {
    if (!isDropdownOpen) return;

    updateDropdownPosition();
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);

    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isDropdownOpen, updateDropdownPosition]);

  // Auto-scroll to selected country when dropdown opens
  useEffect(() => {
    if (!isDropdownOpen || !countryCode) return;

    // Use setTimeout to ensure options are fully rendered
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (dropdownListRef.current) {
          const selectedItem = dropdownListRef.current.querySelector(
            `[role="option"][aria-selected="true"]`
          ) as HTMLElement;
          if (selectedItem) {
            selectedItem.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth'
            });
          }
        }
      }, 50); // Small delay to ensure options are rendered
    });
  }, [isDropdownOpen, countryCode]);

  const handleCountrySelect = (country: CountryOption) => {
    onChangeCountryCode?.(country.code);
    onChangeDialCode?.(country.dialCode);
    setIsDropdownOpen(false);
    inputRef.current?.focus();
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    onChangePhoneNumber?.(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === KEYBOARD_KEYS.ESCAPE) {
      setIsDropdownOpen(false);
    }
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsInputFocused(true);
    onFocus?.(e);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsInputFocused(false);
    onBlur?.(e);
  };

  const handleButtonMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent the button from receiving focus
    e.preventDefault();
  };

  const hasError = Boolean(error);

  // Error visibility: hide when focused (match Omega pattern)
  const showError = hasError && !isInputFocused;

  // Helper text: show when no error OR when focused (if helper text exists)
  const showHelperText = (!hasError || isInputFocused) && helperText;

  return (
    <div
      className={cn(
        DEFAULT_STYLES.field,
        className,
        'base__phone__number__field'
      )}
    >
      {label && (
        <label
          htmlFor={inputId}
          className={cn(
            DEFAULT_STYLES.field__label,
            isDisabled && DEFAULT_STYLES.field__labelDisabled,
            labelClassName
          )}
        >
          {label}
          {isRequired && (
            <span className={DEFAULT_STYLES.field__labelRequired}>*</span>
          )}
        </label>
      )}

      {/* Main wrapper - flex-col for mobile ordering, contains all elements */}
      <div className="flex flex-col">
        {/* Desktop wrapper - flex-row for input+button side by side (hidden on mobile) */}
        {hasExternalButton && (
          <div
            key={`external-button-wrapper-${hasExternalButton}`}
            className="hidden sm:flex sm:flex-row sm:gap-2 sm:items-stretch order-1"
          >
            {/* Input Container - desktop (inside wrapper) */}
            <div
              className={cn(
                DEFAULT_STYLES.field__inputContainer,
                showError
                  ? 'border-errorRed'
                  : DEFAULT_STYLES.field__inputContainerDefault,
                isInputFocused &&
                  !showError &&
                  'border-lightIndigo600 ring-1 ring-lightIndigo600 input-container-focused',
                isDropdownOpen &&
                  !hasError &&
                  !isInputFocused &&
                  'border-lightIndigo600 ring-1 ring-lightIndigo600 input-container-focused',
                isDisabled && DEFAULT_STYLES.field__inputContainerDisabled,
                'sm:flex-1',
                phoneFieldContainerClassName
              )}
            >
              {/* Country Code Selector */}
              <button
                ref={triggerRef}
                type="button"
                onClick={() =>
                  !isDisabled && setIsDropdownOpen(!isDropdownOpen)
                }
                onMouseDown={handleButtonMouseDown}
                disabled={isDisabled}
                tabIndex={-1}
                className={cn(
                  'flex items-center gap-1 self-stretch rounded-l-lg text-sm bg-transparent transition-colors duration-200 -ml-4 pl-4 pr-0',
                  isDisabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer',
                  'focus:bg-transparent focus:outline-none active:bg-transparent',
                  phoneFieldCountryBtnClassName
                )}
                aria-haspopup="listbox"
                aria-expanded={isDropdownOpen}
              >
                <span
                  className={cn(
                    'text-slateText font-medium',
                    phoneFieldCountryTextClassName
                  )}
                >
                  {selectedCountry?.code}
                </span>
                <svg
                  className={cn(
                    'w-4 h-4 text-cement mb-auto flex mt-auto transition-transform duration-200 ease-in-out',
                    phoneFieldDropdownIconClassName
                  )}
                  style={{
                    transform: isDropdownOpen
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                    transformOrigin: 'center'
                  }}
                  fill="none"
                  stroke="#475569"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Phone Number Input */}
              <input
                ref={inputRef}
                id={inputId}
                name={name}
                type="tel"
                value={phoneNumber}
                placeholder={placeholder}
                onChange={handlePhoneNumberChange}
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                disabled={isDisabled}
                aria-invalid={hasError}
                aria-describedby={hasError ? `${inputId}-error` : undefined}
                className={cn(
                  DEFAULT_STYLES.field__input,
                  DEFAULT_STYLES.field__inputText,
                  DEFAULT_STYLES.field__inputPlaceholder,
                  rightElement || rightButtonText
                    ? 'rounded-r-none'
                    : 'rounded-r-lg',
                  isDisabled && DEFAULT_STYLES.field__inputDisabled,
                  inputClassName
                )}
              />

              {/* Right Element - inside input container */}
              {rightElement && (
                <div
                  className={cn(
                    DEFAULT_STYLES.field__sideElement,
                    'pr-2 rounded-r-lg'
                  )}
                >
                  {rightElement}
                </div>
              )}

              {/* Right Button - inside input container (replaces question mark icon) */}
              {rightButtonText && !rightElement && (
                <button
                  type="button"
                  onClick={rightButtonAction}
                  disabled={isDisabled}
                  className={cn(
                    'flex-shrink-0 px-3 py-2 text-sm font-medium rounded-r-lg',
                    'text-royalBlue hover:bg-zumthor transition-colors',
                    'focus:outline-none focus:ring-2 focus:ring-lightIndigo600',
                    isDisabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {rightButtonText}
                </button>
              )}
            </div>

            {/* External Button - desktop (inside wrapper) */}
            {externalButtonElement != null ? (
              <div
                key={`external-button-element-${externalButtonElement != null}`}
                className={cn(
                  'flex items-center flex-shrink-0',
                  externalButtonClassName
                )}
              >
                {externalButtonElement}
              </div>
            ) : externalButtonText != null ? (
              <div
                className={cn(
                  'flex items-center flex-shrink-0',
                  externalButtonClassName
                )}
              >
                <button
                  type="button"
                  onClick={externalButtonAction}
                  disabled={isDisabled}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-lg',
                    'bg-royalBlue text-white hover:bg-darkCerulean',
                    'transition-colors focus:outline-none focus:ring-2 focus:ring-lightIndigo600',
                    isDisabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {externalButtonText}
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* Input Container - mobile (order-1, hidden on desktop if button exists) */}
        <div
          className={cn(
            DEFAULT_STYLES.field__inputContainer,
            showError
              ? 'border-errorRed ring-errorRed ring-opacity-50'
              : DEFAULT_STYLES.field__inputContainerDefault,
            isInputFocused &&
              !showError &&
              'border-lightIndigo600 ring-1 ring-lightIndigo600 input-container-focused',
            isDropdownOpen &&
              !hasError &&
              !isInputFocused &&
              'border-lightIndigo600 ring-1 ring-lightIndigo600 input-container-focused',
            isDisabled && DEFAULT_STYLES.field__inputContainerDisabled,
            // On mobile: order-1 (first), on desktop: hidden if button exists
            'order-1',
            hasExternalButton && 'sm:hidden',
            !hasExternalButton,
            phoneFieldContainerClassName
          )}
        >
          {/* Country Code Selector */}
          <button
            ref={triggerRef}
            type="button"
            onClick={() => !isDisabled && setIsDropdownOpen(!isDropdownOpen)}
            onMouseDown={handleButtonMouseDown}
            disabled={isDisabled}
            tabIndex={-1}
            className={cn(
              'flex items-center gap-1 self-stretch rounded-l-lg text-sm bg-transparent transition-colors duration-200 -ml-4 pl-4 pr-0',
              isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              'focus:bg-transparent focus:outline-none active:bg-transparent',
              phoneFieldCountryBtnClassName
            )}
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
          >
            <span
              className={cn(
                'text-slateText font-medium',
                phoneFieldCountryTextClassName
              )}
            >
              {selectedCountry?.code}
            </span>
            <svg
              className={cn(
                'w-4 h-4 text-cement mb-auto flex mt-auto transition-transform duration-200 ease-in-out',
                phoneFieldDropdownIconClassName
              )}
              style={{
                transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transformOrigin: 'center'
              }}
              fill="none"
              stroke="#475569"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Phone Number Input */}
          <input
            ref={inputRef}
            id={inputId}
            name={name}
            type="tel"
            value={phoneNumber}
            placeholder={placeholder}
            onChange={handlePhoneNumberChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            disabled={isDisabled}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${inputId}-error` : undefined}
            className={cn(
              DEFAULT_STYLES.field__input,
              DEFAULT_STYLES.field__inputText,
              DEFAULT_STYLES.field__inputPlaceholder,
              rightElement || rightButtonText
                ? 'rounded-r-none'
                : 'rounded-r-lg',
              isDisabled && DEFAULT_STYLES.field__inputDisabled,
              inputClassName
            )}
          />

          {/* Right Element - inside input container */}
          {rightElement && (
            <div
              className={cn(
                DEFAULT_STYLES.field__sideElement,
                'pr-2 rounded-r-lg'
              )}
            >
              {rightElement}
            </div>
          )}

          {/* Right Button - inside input container (replaces question mark icon) */}
          {rightButtonText && !rightElement && (
            <button
              type="button"
              onClick={rightButtonAction}
              disabled={isDisabled}
              className={cn(
                'flex-shrink-0 px-3 py-2 text-sm font-medium rounded-r-lg',
                'text-royalBlue hover:bg-zumthor transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-lightIndigo600',
                isDisabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              {rightButtonText}
            </button>
          )}

          {/* Country Dropdown - Rendered via Portal to avoid overflow issues */}
          {isDropdownOpen && (
            <Portal>
              <div
                ref={dropdownRef}
                className={cn(
                  'w-64 bg-white mt-2 border border-lightBlueGray300 rounded-lg shadow-lg overflow-hidden',
                  phoneFieldDropdownClassName,
                  'base__phone__number__dropdown'
                )}
                style={dropdownStyle}
                role="listbox"
                onKeyDown={handleKeyDown}
              >
                {/* Country List */}
                <div
                  ref={dropdownListRef}
                  className={cn(
                    'max-h-48 overflow-y-auto',
                    phoneFieldDropdownListClassName
                  )}
                >
                  {COUNTRIES.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleCountrySelect(country)}
                      className={cn(
                        'w-full flex items-center gap-2 px-4 py-3 text-sm text-left hover:bg-catskillWhite1 transition-colors duration-150',
                        country.code === countryCode &&
                          'bg-zumthor text-royalBlue',
                        phoneFieldOptionBtnClassName
                      )}
                      role="option"
                      aria-selected={country.code === countryCode}
                    >
                      <span
                        className={cn(
                          'flex-1 text-slateText',
                          phoneFieldCountryNameClassName
                        )}
                      >
                        ({country.dialCode}) {country.name}
                      </span>
                      {country.code === countryCode && (
                        <svg
                          className={cn(
                            'w-4 h-4 text-royalBlue',
                            phoneFieldCheckmarkClassName
                          )}
                          fill="none"
                          stroke="#475569"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </Portal>
          )}
        </div>

        {/* Single feedback container - always rendered to prevent layout shift */}
        {/* On mobile: order-2 (between input and button), on desktop: order-2 (below input+button row) */}
        <p
          id={`${inputId}-error`}
          className={cn(
            DEFAULT_STYLES.field__message,
            // On mobile: appears between input and button (order-2)
            // On desktop: appears below input+button row (order-2)
            'order-2',
            showError
              ? cn('text-errorTextColor', phoneFieldErrorTextClassName)
              : showHelperText
                ? cn('text-helperTextColor', phoneFieldHelperTextClassName)
                : cn(phoneFieldHelperTextClassName, 'invisible h-4')
          )}
          role={showError ? 'alert' : undefined}
        >
          {showError ? error : showHelperText ? helperText : ''}
        </p>

        {/* External Button - mobile only (order-3, after message) */}
        {externalButtonElement != null ? (
          <div
            key={`external-button-mobile-${externalButtonElement != null}`}
            className={cn(
              'flex-col sm:hidden items-center w-full order-3',
              externalButtonClassName
            )}
          >
            {externalButtonElement}
          </div>
        ) : externalButtonText != null ? (
          <div
            className={cn(
              'flex-col sm:hidden items-center w-full order-3',
              externalButtonClassName
            )}
          >
            <button
              type="button"
              onClick={externalButtonAction}
              disabled={isDisabled}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-lg w-full',
                'bg-royalBlue text-white hover:bg-darkCerulean',
                'transition-colors focus:outline-none focus:ring-2 focus:ring-lightIndigo600',
                isDisabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              {externalButtonText}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

PhoneNumberField.displayName = 'PhoneNumberField';
